<template>
  <div class="email-manager">
    <!-- Panel de Lista de Emails -->
    <div class="email-list-panel">
      <!-- Header -->
      <div class="list-header">
        <span class="header-title">Bandeja de entrada</span>
        <span class="header-count">{{ filteredEmails.length }} correos</span>
      </div>

      <!-- Barra de búsqueda -->
      <div class="search-row">
        <button @click="abrirModalNuevoEmail" class="btn-nuevo" title="Nuevo mensaje">
          <i class="fa-solid fa-plus"></i>
        </button>
        <div class="search-box">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar correos..."
            class="search-input"
          />
        </div>
        <button
          v-if="searchQuery !== ''"
          @click="buscarGmail"
          class="btn-search-google"
          :disabled="buscandoGmail"
        >
          <i v-if="buscandoGmail" class="fa-solid fa-spinner fa-spin"></i>
          <i v-else class="fa-brands fa-google"></i>
        </button>
        <button
          @click="readFilter = !readFilter"
          class="btn-filter"
          :class="{ active: readFilter }"
        >
          <i v-if="readFilter" class="fa-regular fa-envelope"></i>
          <i v-else class="fa-regular fa-envelope-open"></i>
        </button>
      </div>

      <!-- Labels -->
      <div v-if="labelSeleccionado" class="label-badge-row">
        <span
          class="label-badge"
          :style="{
            backgroundColor: getLabelColor(labelSeleccionado).backgroundColor,
            color: getLabelColor(labelSeleccionado).color,
          }"
        >
          <i class="fa-regular fa-bookmark"></i>
          {{ getLabelName(labelSeleccionado) }}
          ({{ getLabelCantidad(labelSeleccionado) }}/{{ getLabelTotal(labelSeleccionado) }})
        </span>
      </div>

      <!-- Lista de emails -->
      <div class="email-items">
        <div v-if="cargandoCorreos" class="loading-container">
          <div class="spinner"></div>
        </div>

        <div v-else-if="!filteredEmails.length" class="empty-state">
          <i class="fa-regular fa-envelope empty-icon"></i>
          <span>No se encontraron correos</span>
        </div>

        <div
          v-for="email in filteredEmails"
          :key="email.id"
          class="email-item"
          :class="{
            selected: selectedEmail && selectedEmail.id === email.id,
            unread: email.leido === 0,
            'from-search': email.vieneDeBusqueda,
          }"
          @click="openEmail(email)"
        >
          <div class="email-item-top">
            <div class="sender-info">
              <i
                v-if="email.chatgpt && email.chatgpt.tono === 'positivo'"
                class="fa-regular fa-face-grin-beam tone-icon tone-positive"
              ></i>
              <i
                v-if="email.chatgpt && email.chatgpt.tono === 'negativo'"
                class="fa-regular fa-face-angry tone-icon tone-negative"
              ></i>
              <i
                v-if="email.chatgpt && email.chatgpt.tono === 'neutral'"
                class="fa-regular fa-face-meh tone-icon tone-neutral"
              ></i>
              <span class="sender-name">{{ extractName(email.from) }}</span>
            </div>
            <div class="email-meta">
              <span class="email-time">{{ formatDate(email.data.internalDate) }}</span>
              <span
                v-for="label in labels.filter(
                  (l) => email.data.labelIds.includes(l.id) && l.id !== 'INBOX'
                )"
                :key="label.id"
                class="email-label"
                :style="getLabelColor(label.id)"
              >
                {{ label.name }}
              </span>
            </div>
          </div>
          <div class="email-subject">{{ email.subject || '(Sin asunto)' }}</div>
          <div class="email-preview">
            {{ truncateText(stripHtml(email.body), 120) || '(sin contenido)' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Panel de Vista de Email -->
    <div class="email-view-panel">
      <template v-if="selectedEmail">
        <!-- Header con acciones -->
        <div class="view-header">
          <div class="view-actions">
            <button @click="openModalEnviar(selectedEmail)" class="action-btn">
              <i class="fa-solid fa-reply"></i>
              <span>Responder</span>
            </button>
            <button @click="loadThread(selectedEmail)" :disabled="loadingThread" class="action-btn">
              <i v-if="loadingThread" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-comments"></i>
              <span>{{ showThread ? 'Ocultar hilo' : 'Ver hilo' }}</span>
            </button>
            <button
              @click="$bvModal.show('modal-shopify-orders')"
              :disabled="loadingShopifyOrders"
              class="action-btn"
            >
              <i v-if="loadingShopifyOrders" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-bag-shopping"></i>
              <span>{{ loadingShopifyOrders ? 'Buscando pedidos...' : `Pedidos (${shopifyOrders.length})` }}</span>
            </button>
          </div>
          <div class="more-actions">
            <button
              v-if="selectedEmail.leido === 0"
              @click="marcarComoLeido"
              class="action-btn"
            >
              <i class="fa-solid fa-envelope-open"></i>
              <span>Marcar leído</span>
            </button>
            <button v-else @click="marcarComoNoLeido" class="action-btn primary">
              <i class="fa-solid fa-envelope"></i>
              <span>Marcar no leído</span>
            </button>
          </div>
        </div>

        <!-- Contenido del email -->
        <div class="email-content">
          <!-- Info del remitente -->
          <div class="sender-row">
            <div class="avatar">
              {{ getInitials(selectedEmail.from) }}
            </div>
            <div class="sender-details">
              <span class="sender-full-name">{{ extractName(selectedEmail.from) }}</span>
              <span class="sender-email">{{ extractMail(selectedEmail.from) }}</span>
            </div>
            <span class="email-date">{{ formatDate(selectedEmail.data.internalDate) }}</span>
          </div>

          <!-- Asunto -->
          <h2 class="email-title">{{ selectedEmail.subject || '(Sin asunto)' }}</h2>

          <div class="divider"></div>

          <!-- Panel de ChatGPT/Chloe -->
          <div class="ai-panel">
            <div v-if="!waitingChatGpt">
              <button @click="askChatGpt()" class="btn-ai">
                <i class="fa-brands fa-openai"></i>
                Analizar con ChloeGPT
              </button>

              <div v-if="selectedEmail.chatgpt && selectedEmail.chatgpt.resumen" class="ai-results">
                <p><strong>Resumen:</strong> {{ selectedEmail.chatgpt.resumen }}</p>
                <p><strong>Acción sugerida:</strong> {{ selectedEmail.chatgpt.accion_sugerida }}</p>

                <div
                  v-if="
                    selectedEmail.chatgpt.accion_sugerida !== 'ignorar' &&
                    selectedEmail.chatgpt.respuesta_aconsejada
                  "
                  class="ai-response"
                >
                  <strong>Respuesta Aconsejada:</strong>
                  <div v-if="!waitingNewAsk" v-html="formattedRespuesta" class="response-text"></div>
                  <div v-else class="loading-ai">
                    <i class="fa-brands fa-openai fa-spin"></i>
                  </div>
                </div>

                <div v-if="!waitingNewAsk" class="ai-input-group">
                  <i class="fa-brands fa-openai input-icon"></i>
                  <input
                    v-model="newAsk"
                    type="text"
                    placeholder="Nuevas órdenes sobre respuesta aconsejada"
                    @keyup.enter="enviarNuevaOrden"
                    class="ai-input"
                  />
                  <button
                    v-if="newAsk.trim() !== ''"
                    @click="enviarNuevaOrden"
                    class="btn-send"
                  >
                    <i class="fa-solid fa-arrow-up"></i>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="loading-ai">
              <i class="fa-brands fa-openai fa-spin"></i>
            </div>
          </div>

          <!-- Hilo de conversación -->
          <div v-if="showThread" class="thread-container">
            <div class="thread-header">
              <h4><i class="fa-solid fa-comments"></i> Hilo de conversación ({{ threadMessages.length }} mensajes)</h4>
              <button @click="showThread = false" class="btn-close-thread">
                <i class="fa-solid fa-times"></i>
              </button>
            </div>

            <div v-if="loadingThread" class="loading-container">
              <div class="spinner"></div>
            </div>

            <div v-else class="thread-messages">
              <div
                v-for="msg in threadMessages"
                :key="msg.id"
                class="thread-message"
                :class="{ current: msg.id === selectedEmail.idGmail }"
              >
                <div class="thread-msg-header">
                  <div class="thread-sender">
                    <strong>{{ extractName(msg.from) }}</strong>
                    <span class="thread-email">{{ extractMail(msg.from) }}</span>
                  </div>
                  <span class="thread-date">{{ formatDate(msg.internalDate) }}</span>
                </div>
                <div class="thread-subject">{{ msg.subject }}</div>
                <iframe class="thread-iframe" :srcdoc="htmlOplano({cuerpo: msg.body})"></iframe>
              </div>
            </div>
          </div>

          <!-- Adjuntos -->
          <div v-if="selectedEmail.attachments && selectedEmail.attachments.length" class="attachments">
            <strong>Adjuntos:</strong>
            <div class="attachment-list">
              <div
                v-for="(att, index) in selectedEmail.attachments"
                :key="index"
                class="attachment-item"
              >
                <template v-if="att.mimeType.startsWith('image/') && att.url">
                  <img
                    :src="att.url"
                    @click="openImage(att)"
                    class="attachment-thumb"
                  />
                </template>
                <template v-else>
                  <a href="#" @click.prevent="descargarAdjunto(selectedEmail, att)" class="attachment-link">
                    <i class="fa-solid fa-paperclip"></i>
                    {{ att.filename }}
                  </a>
                </template>
              </div>
            </div>
          </div>

          <!-- Cuerpo del email -->
          <iframe
            class="email-iframe"
            :srcdoc="htmlOplano(selectedEmail)"
          ></iframe>

          <!-- Botón responder al final -->
          <div class="reply-footer">
            <button @click="openModalEnviar(selectedEmail)" class="btn-reply">
              <i class="fa-solid fa-reply"></i>
              Responder
            </button>
          </div>
        </div>
      </template>

      <!-- Estado vacío -->
      <div v-else class="empty-view">
        <i class="fa-regular fa-envelope empty-icon-large"></i>
        <h3>Selecciona un correo para ver su contenido</h3>
      </div>
    </div>

    <!-- Modal de respuesta -->
    <b-modal id="modal-email" centered size="xl" hide-footer hide-header class="modal-max-height">
      <div class="reply-modal">
        <div class="reply-modal-header">
          <div class="reply-header-info">
            <span class="reply-title"><i class="fa-solid fa-reply"></i> Responder</span>
            <span v-if="destinatarioRespuesta" class="reply-to-chip">
              <i class="fa-regular fa-paper-plane"></i>
              <span>Para:&nbsp;<strong>{{ destinatarioRespuesta }}</strong></span>
            </span>
          </div>
          <button class="reply-close" @click="$bvModal.hide('modal-email')">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <b-row>
          <b-col cols="3">
            <div class="plantillas-panel">
              <div class="plantillas-header">
                <span class="plantillas-title">Plantillas</span>
                <span class="plantillas-count">{{ filterPlantillas.length }}</span>
              </div>
              <div class="plantillas-search">
                <i class="fa-solid fa-magnifying-glass"></i>
                <input type="text" placeholder="Buscar plantilla..." v-model="buscarPlantilla" />
              </div>
              <button
                v-if="selectedEmail && selectedEmail.chatgpt && selectedEmail.chatgpt.respuesta_aconsejada"
                class="btn-chloe"
                @click="usarRespuestaChloe"
              >
                <i class="fa-solid fa-wand-magic-sparkles"></i> Usar respuesta Chloe
              </button>
              <div class="scroll-listado plantillas-list">
                <div
                  v-for="(plantilla, index) in filterPlantillas"
                  :key="index"
                  class="plantilla-item"
                  @click="selectPlantilla(plantilla)"
                >
                  <span class="plantilla-nombre">{{ plantilla.nombre }}</span>
                  <button class="plantilla-ai" title="Adaptar con IA" @click.stop="plantillaChloe(plantilla)">
                    <i class="fa-brands fa-openai"></i>
                  </button>
                </div>
                <div v-if="filterPlantillas.length === 0" class="plantillas-empty">
                  <i class="fa-regular fa-folder-open"></i> Sin resultados
                </div>
              </div>
            </div>
          </b-col>
          <b-col md="9" v-if="!waitingPlantillaChloe">
            <quill-editor v-model="contentMail" :options="editorOption" ref="quillEditor" />
            <b-form-group label="Adjuntar PDF, JPG o PNG (Opcional):" label-for="file-input-multi" class="mt-1">
              <b-form-file
                id="file-input-multi"
                v-model="archivosAdjuntosTemp"
                placeholder="Selecciona o arrastra archivos aquí..."
                drop-placeholder="Suelta el archivo aquí..."
                accept=".pdf, .png, .jpg, .jpeg"
                multiple
                @input="handleFileUpload"
              ></b-form-file>
            </b-form-group>

            <div v-if="adjuntos.length > 0" class="mb-3">
              <h6>Archivos adjuntos:</h6>
              <b-list-group>
                <b-list-group-item
                  v-for="(file, index) in adjuntos"
                  :key="index"
                  class="d-flex justify-content-between align-items-center list-group-item-sm p-2"
                >
                  <span><i class="fa fa-file-pdf mr-2"></i> {{ file.name }}</span>
                  <b-button
                    variant="danger"
                    size="sm"
                    @click="removeAttachment(index)"
                    class="ml-2"
                    title="Eliminar adjunto"
                  >
                    <i class="fa fa-times"></i>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </div>

            <div class="reply-send-row">
              <b-button variant="primary" @click="enviarEmail">
                <i class="fa-regular fa-paper-plane mr-1"></i> Enviar Email
              </b-button>
            </div>
          </b-col>
          <b-col md="9" v-else class="d-flex justify-content-center align-items-center" style="height: 200px">
            <i class="fa-brands fa-openai fa-spin-custom text-primary" style="font-size: 3rem"></i>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- Modal Nuevo Email -->
    <b-modal id="modal-nuevo-email" title="Nuevo Mensaje" centered size="xl" hide-footer class="modal-max-height">
      <b-row>
        <b-col cols="3">
          <div>
            <h5>Plantillas</h5>
            <b-form-input
              type="text"
              placeholder="Buscar plantilla..."
              v-model="buscarPlantillaNuevo"
              class="mb-2"
            />
            <div class="scroll-listado">
              <b-list-group>
                <b-list-group-item
                  v-for="(plantilla, index) in filterPlantillasNuevo"
                  :key="index"
                  class="d-flex justify-content-between align-items-center group-item-email"
                  @click="selectPlantillaNuevo(plantilla)"
                >
                  <div class="flex-grow-1">{{ plantilla.nombre }}</div>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
        </b-col>
        <b-col cols="9">
          <b-form @submit.prevent="enviarNuevoEmail">
            <b-form-group label="Para:" label-for="nuevo-email-to">
              <b-form-input id="nuevo-email-to" v-model="nuevoEmail.to" type="email" placeholder="destinatario@email.com" required />
            </b-form-group>

            <b-form-group label="Asunto:" label-for="nuevo-email-subject">
              <b-form-input id="nuevo-email-subject" v-model="nuevoEmail.subject" placeholder="Asunto del mensaje" required />
            </b-form-group>

            <b-form-group label="Mensaje:">
              <quill-editor v-model="nuevoEmail.body" :options="editorOption" style="min-height: 300px;" class="editor-nuevo-email" />
            </b-form-group>

            <b-form-group label="Adjuntar archivos (Opcional):">
              <b-form-file v-model="nuevoEmailAdjuntosTemp" placeholder="Selecciona archivos..." accept=".pdf, .png, .jpg, .jpeg" multiple @input="handleNuevoEmailFileUpload" />
            </b-form-group>

            <div v-if="nuevoEmailAdjuntos.length > 0" class="mb-3">
              <h6>Archivos adjuntos:</h6>
              <b-list-group>
                <b-list-group-item v-for="(file, index) in nuevoEmailAdjuntos" :key="index" class="d-flex justify-content-between align-items-center p-2">
                  <span><i class="fa fa-file mr-2"></i> {{ file.name }}</span>
                  <b-button variant="danger" size="sm" @click="removeNuevoEmailAttachment(index)">
                    <i class="fa fa-times"></i>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </div>

            <div class="d-flex justify-content-end mt-3">
              <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('modal-nuevo-email')">Cancelar</b-button>
              <b-button type="submit" variant="primary" :disabled="enviandoNuevoEmail">
                <span v-if="enviandoNuevoEmail"><i class="fa fa-spinner fa-spin mr-1"></i> Enviando...</span>
                <span v-else><i class="fa fa-paper-plane mr-1"></i> Enviar</span>
              </b-button>
            </div>
          </b-form>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Modal Pedidos Shopify -->
    <b-modal id="modal-shopify-orders" size="lg" hide-footer :title="`Pedidos Shopify de ${selectedEmail ? extractMail(selectedEmail.from) : ''}`">
      <div v-if="shopifyOrders.length === 0" class="text-center p-3 text-muted">
        No hay pedidos para este email.
      </div>
      <div v-else>
        <div v-for="order in shopifyOrders" :key="order.id" class="shopify-order-card">
          <div class="shopify-order-header">
            <div class="shopify-order-header-left">
              <a :href="order.adminUrl" target="_blank" class="shopify-order-name">
                {{ order.name }} <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
              <span class="shopify-order-date">{{ new Date(order.createdAt).toLocaleDateString('es-ES') }}</span>
            </div>
            <div class="shopify-order-header-right">
              <b-badge :variant="order.financialStatus === 'PAID' ? 'success' : 'warning'">{{ order.financialStatus }}</b-badge>
              <b-badge :variant="order.fulfillmentStatus === 'FULFILLED' ? 'success' : 'secondary'">{{ order.fulfillmentStatus }}</b-badge>
              <strong class="shopify-order-total">{{ order.total }} {{ order.currency }}</strong>
            </div>
          </div>
          <div v-for="(item, idx) in order.lineItems" :key="idx" class="shopify-line-item">
            <div class="shopify-line-thumb">
              <img v-if="item.image" :src="item.image" :alt="item.title" />
              <i v-else class="fa-solid fa-image text-muted"></i>
            </div>
            <div class="shopify-line-info">
              <div class="shopify-line-title">{{ item.title }}</div>
              <div class="shopify-line-meta">
                <span v-if="item.variantTitle && item.variantTitle !== 'Default Title'" class="shopify-line-variant">{{ item.variantTitle }}</span>
                <span v-if="item.sku" class="shopify-line-sku">{{ item.sku }}</span>
              </div>
              <div v-for="(tracking, tIdx) in item.trackings" :key="tIdx" class="shopify-tracking">
                <i class="fa-solid fa-truck"></i>
                <span>{{ tracking.company || 'Tracking' }}:</span>
                <a v-if="tracking.url" :href="tracking.url" target="_blank">{{ tracking.number }}</a>
                <span v-else>{{ tracking.number }}</span>
              </div>
            </div>
            <div class="shopify-line-prices">
              <span class="shopify-line-unit">
                <s v-if="parseFloat(item.originalUnitPrice) > parseFloat(item.unitPrice)" class="shopify-line-original">{{ item.originalUnitPrice }}</s>
                {{ item.unitPrice }} {{ item.currency }} × {{ item.quantity }}
              </span>
              <span class="shopify-line-total">{{ (parseFloat(item.unitPrice) * item.quantity).toFixed(2) }} {{ item.currency }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "@axios";
import {
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BFormCheckbox,
  BMedia,
  BMediaAside,
  BMediaBody,
  BAvatar,
} from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import BCardCode from "@core/components/b-card-code";
import { quillEditor } from "vue-quill-editor";

export default {
  name: "EmailManager",
  components: {
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormCheckbox,
    BMedia,
    BMediaAside,
    BMediaBody,
    BAvatar,
    VuePerfectScrollbar,
    BCardCode,
    quillEditor,
  },
  data() {
    return {
      labelColors: {
        personal: "success",
        company: "primary",
        important: "warning",
        private: "danger",
      },
      emails: [],
      searchQuery: "",
      lastSearchQuery: "",
      countSameSearch: 0,
      colors: ["#f87171", "#60a5fa", "#34d399", "#fbbf24", "#a78bfa", "#f472b6"],
      showModal: false,
      selectedEmail: null,
      expandedEmailId: null,
      labels: [{ id: "INBOX", name: "INBOX", type: "system" }],
      waitingChatGpt: false,
      labelSeleccionado: "INBOX",
      readFilter: false,
      newAsk: "",
      threadMessages: [],
      loadingThread: false,
      shopifyOrders: [],
      loadingShopifyOrders: false,
      showThread: false,
      adjuntos: [],
      archivosAdjuntosTemp: null,
      waitingNewAsk: false,
      cargandoCorreos: false,
      contentMail: ``,
      editorOption: {
        theme: "snow",
        modules: {
          toolbar: {
            container: [
              ["bold", "italic", "underline", "strike"],
              ["blockquote", "code-block"],
              ["link", "image", "video", "formula"],
              [{ header: 1 }, { header: 2 }],
              [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
              [{ script: "sub" }, { script: "super" }],
              [{ indent: "-1" }, { indent: "+1" }],
              [{ direction: "rtl" }],
              [{ size: ["small", false, "large", "huge"] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ color: [] }, { background: [] }],
              [{ font: [] }],
              [{ align: [] }],
              ["clean"],
            ],
            imageResize: true,
            handlers: {
              imageUrl: function () {
                const url = prompt("Introduce la URL de la imagen");
                if (url) {
                  const range = this.quill.getSelection();
                  this.quill.insertEmbed(range.index, "image", url);
                  const imageElement = this.quill.root.querySelector(`img[src="${url}"]`);
                  if (imageElement) {
                    imageElement.style.maxWidth = "500px";
                  }
                }
              },
            },
          },
        },
      },
      plantillaSelect: { id: 0, nombre: "", contenido: "", explicacion: "" },
      plantillas: [],
      buscarPlantilla: "",
      waitingPlantillaChloe: false,
      buscandoGmail: false,
      interval: null,
      // Nuevo email
      nuevoEmail: {
        to: '',
        subject: '',
        body: ''
      },
      nuevoEmailAdjuntos: [],
      nuevoEmailAdjuntosTemp: null,
      enviandoNuevoEmail: false,
      buscarPlantillaNuevo: '',
    };
  },
  computed: {
    filteredEmails() {
      const q = this.searchQuery.toLowerCase();
      const label = this.labelSeleccionado;
      const read = this.readFilter ? "UNREAD" : "";

      return this.emails.filter((email) => {
        const body = (email.body || "").toLowerCase();
        const from = (email.from || "").toLowerCase();
        const subject = (email.subject || "").toLowerCase();

        const matchesQuery = body.includes(q) || from.includes(q) || subject.includes(q);
        const matchesLabel = !label || (email.data && email.data.labelIds && email.data.labelIds.includes(label));
        const matchesRead = !read || (email.data && email.data.labelIds && email.data.labelIds.includes(read));
        return matchesQuery && matchesLabel && matchesRead;
      });
    },
    filterPlantillas() {
      const q = this.buscarPlantilla.toLowerCase();
      return this.plantillas.filter((pl) => {
        return pl.nombre.toLowerCase().includes(q) || pl.contenido.toLowerCase().includes(q);
      });
    },
    filterPlantillasNuevo() {
      const q = this.buscarPlantillaNuevo.toLowerCase();
      return this.plantillas.filter((pl) => {
        return pl.nombre.toLowerCase().includes(q) || pl.contenido.toLowerCase().includes(q);
      });
    },
    // Misma lógica que el backend (enviarRespuestaGmail): Reply-To || From,
    // y si es el bot de chat, el email del cliente que viene en el cuerpo
    destinatarioRespuesta() {
      if (!this.selectedEmail) return "";
      const headers =
        (this.selectedEmail.data &&
          this.selectedEmail.data.payload &&
          this.selectedEmail.data.payload.headers) ||
        [];
      const replyTo = headers.find((h) => h.name.toLowerCase() === "reply-to");
      let to = this.extractMail(replyTo ? replyTo.value : this.selectedEmail.from);

      if (to && to.toLowerCase().includes("@chatai.harberlondon.com")) {
        const texto = (this.selectedEmail.body || "").replace(/<[^>]+>/g, " ");
        const match = texto.match(/Email:\s*([\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/i);
        if (match) to = match[1];
      }
      return to;
    },
    formattedRespuesta() {
      if (!this.selectedEmail.chatgpt) return "";
      return this.selectedEmail?.chatgpt?.respuesta_aconsejada
        ? this.selectedEmail.chatgpt.respuesta_aconsejada.replace(/\n/g, "<br>")
        : "";
    },
  },
  methods: {
    stripHtml(html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    openEmail(email) {
      this.selectedEmail = email;
      this.contentMail = email.chatgpt?.respuesta_aconsejada
        ? email.chatgpt.respuesta_aconsejada.replace(/\n/g, "<br>")
        : "";
      this.adjuntos = [];
      this.showThread = false;
      this.threadMessages = [];
      this.loadingThread = false;
      this.buscarPedidosShopify(email);
    },
    async buscarPedidosShopify(email) {
      this.shopifyOrders = [];
      this.loadingShopifyOrders = false;
      const mail = this.extractMail(email.from);
      if (!mail || !mail.includes("@")) return;
      this.loadingShopifyOrders = true;
      try {
        const response = await axios.get("/shopify/orders/by-email", { params: { email: mail } });
        // Evitar pisar resultados si el usuario ya cambió de correo
        if (this.selectedEmail && this.selectedEmail.id === email.id) {
          this.shopifyOrders = response.data;
        }
      } catch (error) {
        console.error("Error buscando pedidos Shopify:", error);
      } finally {
        if (!this.selectedEmail || this.selectedEmail.id === email.id) {
          this.loadingShopifyOrders = false;
        }
      }
    },
    async marcarComoLeido() {
      if (this.selectedEmail.leido === 0) {
        try {
          await axios.post("/ia/email/read/gmail", { idGmail: this.selectedEmail.idGmail });
          this.selectedEmail.leido = 1;
          // Quitar UNREAD de labelIds
          if (this.selectedEmail.data && this.selectedEmail.data.labelIds) {
            const idx = this.selectedEmail.data.labelIds.indexOf('UNREAD');
            if (idx > -1) {
              this.selectedEmail.data.labelIds.splice(idx, 1);
            }
          }
          // Actualizar contador de labels
          this.actualizarContadorLabels();
        } catch (error) {
          console.error('Error al marcar como leído:', error);
          this.$bvToast.toast('Error al marcar como leído', { variant: 'danger', solid: true });
        }
      }
    },
    async marcarComoNoLeido() {
      if (this.selectedEmail.leido === 1) {
        try {
          await axios.post("/ia/email/unread/gmail", { idGmail: this.selectedEmail.idGmail });
          this.selectedEmail.leido = 0;
          // Añadir UNREAD a labelIds
          if (this.selectedEmail.data && this.selectedEmail.data.labelIds) {
            if (!this.selectedEmail.data.labelIds.includes('UNREAD')) {
              this.selectedEmail.data.labelIds.push('UNREAD');
            }
          }
          // Actualizar contador de labels
          this.actualizarContadorLabels();
        } catch (error) {
          console.error('Error al marcar como no leído:', error);
          this.$bvToast.toast('Error al marcar como no leído', { variant: 'danger', solid: true });
        }
      }
    },
    actualizarContadorLabels() {
      this.labels.forEach((label) => {
        label.cantidad = 0;
      });
      this.emails.forEach((email) => {
        if (email.data && email.data.labelIds && email.data.labelIds.includes('UNREAD')) {
          email.data.labelIds.forEach((labelId) => {
            const label = this.labels.find((l) => l.id === labelId);
            if (label) {
              label.cantidad += 1;
            }
          });
        }
      });
    },
    decodeBase64Url(base64Data) {
      if (!base64Data) return "";
      try {
        let base64 = base64Data.replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4) {
          base64 += "=";
        }
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new TextDecoder("utf-8").decode(bytes);
      } catch (e) {
        console.warn("Error decodificando base64:", e);
        return "";
      }
    },
    getThreadMessageBody(payload) {
      let htmlPart = null;
      let textPart = null;

      const findParts = (parts) => {
        if (!parts) return;
        for (const part of parts) {
          if (part.mimeType === "text/html" && part.body?.data) {
            htmlPart = part.body.data;
          } else if (part.mimeType === "text/plain" && part.body?.data) {
            textPart = part.body.data;
          }
          if (part.parts) {
            findParts(part.parts);
          }
        }
      };

      if (payload.parts) {
        findParts(payload.parts);
      } else if (payload.body?.data) {
        const decoded = this.decodeBase64Url(payload.body.data);
        const esHTML = /<\/?[a-z][\s\S]*>/i.test(decoded);
        return esHTML ? decoded : "<pre>" + decoded + "</pre>";
      }

      if (htmlPart) {
        return this.decodeBase64Url(htmlPart);
      } else if (textPart) {
        const decoded = this.decodeBase64Url(textPart);
        return "<pre>" + decoded + "</pre>";
      }

      return "<p>(Sin contenido)</p>";
    },
    async loadThread(email) {
      if (this.showThread) {
        this.showThread = false;
        return;
      }

      this.loadingThread = true;
      this.showThread = true;
      this.threadMessages = [];

      try {
        const threadId = email.data.threadId;
        const response = await axios.get(`/ia/emails/thread/${threadId}`);

        this.threadMessages = response.data.map((msg) => {
          const headers = msg.payload?.headers || [];
          const subject = headers.find((h) => h.name === "Subject")?.value || "(Sin asunto)";
          const from = headers.find((h) => h.name === "From")?.value || "(Desconocido)";
          const body = this.getThreadMessageBody(msg.payload);

          return { id: msg.id, subject, from, body, internalDate: msg.internalDate };
        });

        this.threadMessages.sort((a, b) => Number(a.internalDate) - Number(b.internalDate));
      } catch (error) {
        console.error("Error al cargar el hilo:", error);
        this.$bvToast.toast("Error al cargar el hilo de conversación", {
          variant: "danger",
          solid: true,
        });
        this.showThread = false;
      } finally {
        this.loadingThread = false;
      }
    },
    backToList() {
      this.selectedEmail = null;
    },
    toggleEmail(id) {
      this.expandedEmailId = this.expandedEmailId === id ? null : id;
    },
    openModal(email) {
      this.selectedEmail = email;
      this.showModal = true;
    },
    getColor(from) {
      const index = from.charCodeAt(0) % this.colors.length;
      return this.colors[index];
    },
    getInitials(from) {
      const name = this.extractName(from);
      return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },
    extractName(from) {
      if (!from) return "(desconocido)";
      const match = from.match(/^(.*?)</);
      return match ? match[1].trim() : from;
    },
    extractMail(from) {
      if (!from) return "(desconocido)";
      const match = from.match(/<([^>]+)>/);
      return match ? match[1].trim() : from.trim();
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const timestamp = Number(dateStr);
      const d = new Date(timestamp);
      const today = new Date();
      const isToday =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();

      if (isToday) {
        return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
      } else {
        return d.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    },
    async plantillaChloe(plantilla) {
      this.waitingPlantillaChloe = true;
      const response = await axios.post("/ia/email/analizar/plantilla", {
        correo: this.selectedEmail.cuerpo,
        id: this.selectedEmail.idGmail,
        plantilla: plantilla.contenido,
        from: this.selectedEmail.from,
      });
      this.selectedEmail.chatgpt = JSON.parse(response.data.json);
      this.contentMail = this.selectedEmail.chatgpt.respuesta_aconsejada
        ? this.selectedEmail.chatgpt.respuesta_aconsejada.replace(/\n/g, "<br>")
        : "";
      this.waitingPlantillaChloe = false;
    },
    usarRespuestaChloe() {
      this.contentMail = this.selectedEmail.chatgpt?.respuesta_aconsejada
        ? this.selectedEmail.chatgpt.respuesta_aconsejada.replace(/\n/g, "<br>")
        : "";
    },
    selectPlantilla(plantilla) {
      this.contentMail = plantilla.contenido;
    },
    selectPlantillaNuevo(plantilla) {
      this.nuevoEmail.body = plantilla.contenido;
    },
    async loadItems(autload = false) {
      if (!autload) axios.post("/uso", { seccion: "ia-gmail-v2" });

      if (!autload) {
        this.labels = [{ id: "INBOX", name: "INBOX", type: "system", cantidad: 0, total: 0 }];
        this.cargandoCorreos = true;

        const responseLabels = await axios.get("/ia/emails/labels");
        this.labels.push(
          ...responseLabels.data
            .filter((data) => data.id.includes("Label"))
            .map((data) => ({ ...data, cantidad: 0, total: 0 }))
        );

        const responsePlantillas = await axios.get("/ia/emails/plantillas");
        this.plantillas = responsePlantillas.data;
      }

      // Guardar emails que vinieron de búsqueda para no perderlos
      const emailsDeBusqueda = this.emails?.filter(e => e.vieneDeBusqueda) || [];

      const responseEmails = await axios.get("/ia/emails/cache", { silencioso: autload });
      const rawEmails = responseEmails.data;

      const emailsDelCache = rawEmails.map((email) => {
        const existingEmail = this.emails?.find((e) => e.id === email.data.id);
        const headers = email.data.payload.headers || [];
        const subject = headers.find((h) => h.name === "Subject")?.value || "(Sin asunto)";
        const from = headers.find((h) => h.name === "From")?.value || "(Desconocido)";
        const bodyData = this.getBody(email.data.payload);
        const decodedBody = bodyData ? Buffer.from(bodyData, "base64").toString("utf-8") : "";
        const attachments = this.getAttachmentsFromMessage(email.data);

        return {
          ...email,
          id: email.data.id,
          subject,
          from,
          body: decodedBody,
          attachments,
          vieneDeBusqueda: false,
          chatgpt: existingEmail ? existingEmail.chatgpt : email.chatgpt,
        };
      });

      // Filtrar emails de búsqueda que no estén ya en el cache
      const idsDelCache = new Set(emailsDelCache.map(e => e.id));
      const emailsDeBusquedaUnicos = emailsDeBusqueda.filter(e => !idsDelCache.has(e.id));

      // Combinar: primero los de búsqueda, luego los del cache
      this.emails = [...emailsDeBusquedaUnicos, ...emailsDelCache];

      this.labels.forEach((label) => {
        label.total = 0;
        label.cantidad = 0;
      });

      this.emails.forEach((email) => {
        if (email.data.labelIds) {
          email.data.labelIds.forEach((labelId) => {
            const label = this.labels.find((l) => l.id === labelId);
            if (label) {
              label.total += 1;
              if (email.data.labelIds.includes("UNREAD")) {
                label.cantidad += 1;
              }
            }
          });
        }
      });

      this.cargandoCorreos = false;
    },
    async askChatGpt() {
      this.waitingChatGpt = true;
      const response = await axios.post("/ia/email/analizar", {
        correo: this.selectedEmail.cuerpo,
        id: this.selectedEmail.idGmail,
        from: this.selectedEmail.from,
      });
      this.selectedEmail.chatgpt = JSON.parse(response.data.json);
      this.contentMail = this.selectedEmail.chatgpt?.respuesta_aconsejada
        ? this.selectedEmail.chatgpt.respuesta_aconsejada.replace(/\n/g, "<br>")
        : "";
      this.waitingChatGpt = false;
    },
    async enviarNuevaOrden() {
      this.waitingNewAsk = true;
      const response = await axios.post("/ia/email/analizar/ajustes", {
        correo: this.selectedEmail.cuerpo,
        id: this.selectedEmail.idGmail,
        nuevoAjuste: "Por favor, ajusta la respuesta aconseja. " + this.newAsk,
      });
      this.newAsk = "";
      this.selectedEmail.chatgpt = JSON.parse(response.data.json);
      this.waitingNewAsk = false;
    },
    async marcarLeidoGmail() {
      await axios.post("/ia/email/read/gmail", { idGmail: this.selectedEmail.idGmail });
    },
    htmlOplano(email) {
      const esHTML = /<\/?[a-z][\s\S]*>/i.test(email.cuerpo);
      let html = esHTML ? email.cuerpo : "<pre>" + email.cuerpo + "</pre>";
      // Abrir los enlaces del correo en una pestaña nueva en lugar de dentro del iframe
      // (muchas webs bloquean cargarse en iframes y se quedaban en blanco).
      html = '<base target="_blank">' + html;
      const isDark = document.body.classList.contains('dark-layout');
      if (isDark) {
        const darkStyle = '<style>body{background:#2a2a2a!important;color:#d0d2d6!important;padding:16px!important;border-radius:8px}*{color:inherit!important;border-color:#444!important}a{color:#7367f0!important}table,td,th{background:transparent!important}pre{color:#d0d2d6!important}img{border-radius:4px}</style>';
        html = darkStyle + html;
      }
      return html;
    },
    getAttachmentsFromMessage(message) {
      const attachments = [];

      function findAttachments(parts) {
        for (const part of parts) {
          if (part.filename && part.body && part.body.attachmentId) {
            attachments.push({
              filename: part.filename,
              attachmentId: part.body.attachmentId,
              mimeType: part.mimeType,
              size: part.body.size || 0,
              url: "",
            });
          }
          if (part.parts) {
            findAttachments(part.parts);
          }
        }
      }

      if (message.payload.parts) {
        findAttachments(message.payload.parts);
      }

      return attachments;
    },
    getBody(payload) {
      let body = "";
      if (payload.parts) {
        for (const part of payload.parts) {
          if (part.mimeType === "text/plain" || part.mimeType === "text/html") {
            body += part.body.data || "";
          } else if (part.parts) {
            body += this.getBody(part);
          }
        }
      } else if (payload.body && payload.body.data) {
        body += payload.body.data;
      }
      return body;
    },
    async descargarAdjunto(email, att) {
      try {
        if (!att.url) {
          const response = await axios.get(
            `/ia/email/adjuntos/${email.id}/${att.attachmentId}/${att.filename}`,
            { responseType: "arraybuffer" }
          );
          const blob = new Blob([response.data], { type: att.mimeType });
          att.url = URL.createObjectURL(blob);
        }

        if (att.mimeType.startsWith("image/")) {
          // Para imágenes, se muestra el thumbnail
        } else {
          const a = document.createElement("a");
          a.href = att.url;
          a.download = att.filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } catch (err) {
        console.error("Error al descargar adjunto:", err);
      }
    },
    openImage(att) {
      window.open(att.url, "_blank");
    },
    getLabelName(labelId) {
      const label = this.labels.find((l) => l.id === labelId);
      return label ? label.name : labelId;
    },
    getLabelCantidad(labelId) {
      const label = this.labels.find((l) => l.id === labelId);
      return label && label.cantidad ? label.cantidad : 0;
    },
    getLabelTotal(labelId) {
      const label = this.labels.find((l) => l.id === labelId);
      return label && label.total ? label.total : 0;
    },
    getLabelColor(labelId) {
      const label = this.labels.find((l) => l.id === labelId);
      return {
        backgroundColor: (label && label.color && label.color.backgroundColor) || "#6c757d",
        color: (label && label.color && label.color.textColor) || "#fff",
      };
    },
    openModalEnviar(item) {
      this.$bvModal.show("modal-email");
    },
    async enviarEmail() {
      let contenidoConEstilos = this.contentMail;
      let tempDiv = document.createElement("div");
      tempDiv.innerHTML = contenidoConEstilos;

      let images = tempDiv.querySelectorAll("img");
      images.forEach((img) => {
        img.style.maxWidth = this.maxWidthImg;
      });

      contenidoConEstilos = tempDiv.innerHTML.replaceAll("<p><br></p>", "<p></p>");

      const attachmentsPayload = await Promise.all(
        this.adjuntos.map(async (file) => ({
          data: await this.fileToBase64(file),
          name: file.name,
        }))
      );

      let info = {
        replyHtml: contenidoConEstilos,
        originalEmail: this.selectedEmail,
        adjuntos: attachmentsPayload,
      };

      axios
        .post("/ia/emails/responder", info)
        .then((response) => response.data)
        .then(() => {
          this.$bvToast.toast("Email enviado correctamente", { variant: "success", solid: true });
          this.marcarComoLeido();
          this.$bvModal.hide("modal-email");
        })
        .catch(() => {
          this.$bvToast.toast("Error al mandar el email", { variant: "danger", solid: true });
        });
    },
    // Nuevo Email
    abrirModalNuevoEmail() {
      this.nuevoEmail = { to: '', subject: '', body: `<p><br></p><p><br></p><p>-- </p><p>Chloe </p><p>Harber London </p><p><a href="www.harberlondon.com" rel="noopener noreferrer" target="_blank">www.harberlondon.com</a></p><p><br></p><p> ------------------------------- </p><p><br></p><p>DISCLAIMER: The information contained in this electronic mail message is confidential. This message and attachments have been sent for the exclusive use of the addressee. Access to this message by other individuals is not authorised. Similarly, if you are not the person to whom this message is addressed, any disclosure, copy or distribution of the information is prohibited and could be illegal. If you have received this message by error, please notify the sender immediately, let us know and delete it from your computer.</p>` };
      this.nuevoEmailAdjuntos = [];
      this.nuevoEmailAdjuntosTemp = null;
      this.$bvModal.show('modal-nuevo-email');
    },
    handleNuevoEmailFileUpload() {
      if (this.nuevoEmailAdjuntosTemp) {
        const files = Array.isArray(this.nuevoEmailAdjuntosTemp) ? this.nuevoEmailAdjuntosTemp : [this.nuevoEmailAdjuntosTemp];
        files.forEach(file => {
          if (!this.nuevoEmailAdjuntos.find(f => f.name === file.name)) {
            this.nuevoEmailAdjuntos.push(file);
          }
        });
        this.nuevoEmailAdjuntosTemp = null;
      }
    },
    removeNuevoEmailAttachment(index) {
      this.nuevoEmailAdjuntos.splice(index, 1);
    },
    async enviarNuevoEmail() {

      if (!this.nuevoEmail.to || !this.nuevoEmail.subject || !this.nuevoEmail.body) {
        this.$bvToast.toast("Completa todos los campos", { variant: "warning", solid: true });
        return;
      }

      this.enviandoNuevoEmail = true;

      try {
        const attachmentsPayload = await Promise.all(
          this.nuevoEmailAdjuntos.map(async (file) => ({
            data: await this.fileToBase64(file),
            name: file.name,
          }))
        );

        await axios.post("/ia/emails/nuevo", {
          to: this.nuevoEmail.to,
          subject: this.nuevoEmail.subject,
          body: this.nuevoEmail.body,
          adjuntos: attachmentsPayload,
        });

        this.$bvToast.toast("Email enviado correctamente", { variant: "success", solid: true });
        this.$bvModal.hide('modal-nuevo-email');
        this.nuevoEmail = { to: '', subject: '', body: '' };
        this.nuevoEmailAdjuntos = [];
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Error al enviar el email", { variant: "danger", solid: true });
      } finally {
        this.enviandoNuevoEmail = false;
      }
    },
    buscarGmail() {
      if (this.lastSearchQuery === this.searchQuery) {
        this.countSameSearch++;
      } else {
        this.countSameSearch = 1;
      }

      this.lastSearchQuery = this.searchQuery;
      this.buscandoGmail = true;

      axios
        .post("/ia/emails/buscar", { texto: this.searchQuery, cantidad: this.countSameSearch * 5 })
        .then((response) => response.data)
        .then((busquedaEmails) => {
          const idsExistentes = new Set(this.emails.map((e) => e.id));

          const procesados = busquedaEmails
            .filter((email) => !idsExistentes.has(email.data.id))
            .map((email) => {
              const headers = email.data.payload.headers || [];
              const subject = headers.find((h) => h.name === "Subject")?.value || "(Sin asunto)";
              const from = headers.find((h) => h.name === "From")?.value || "(Desconocido)";
              const bodyData = this.getBody(email.data.payload);
              const decodedBody = bodyData ? Buffer.from(bodyData, "base64").toString("utf-8") : "";
              const attachments = this.getAttachmentsFromMessage(email.data);

              return {
                ...email,
                id: email.data.id,
                subject,
                from,
                body: decodedBody,
                attachments,
                vieneDeBusqueda: true,
              };
            });

          this.emails = [...procesados, ...this.emails];

          this.emails.forEach((email) => {
            if (email.labelIds) {
              email.labelIds.forEach((labelId) => {
                const label = this.labels.find((l) => l.id === labelId);
                if (label) {
                  label.total += 1;
                  if (email.labelIds.includes("UNREAD")) {
                    label.cantidad += 1;
                  }
                }
              });
            }
          });
          this.buscandoGmail = false;
        })
        .catch((err) => {
          console.log(err);
          this.buscandoGmail = false;
          this.$bvToast.toast("Error al buscar en gmail", { variant: "danger", solid: true });
        });
    },
    handleFileUpload(files) {
      if (files && files.length > 0) {
        this.adjuntos.push(...Array.from(files));
        this.archivosAdjuntosTemp = null;
      }
    },
    removeAttachment(index) {
      this.adjuntos.splice(index, 1);
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64String = reader.result.split(",")[1];
          resolve(base64String);
        };
        reader.onerror = (error) => reject(error);
      });
    },
  },
  mounted() {
    this.loadItems();
    this.interval = setInterval(() => {
      this.loadItems(true);
    }, 90000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
};
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;

.email-manager {
  display: flex;
  height: 85vh;
  background: $bg;
  font-family: 'Plus Jakarta Sans', sans-serif;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid $border;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

// Panel de lista de emails
.email-list-panel {
  width: 380px;
  min-width: 380px;
  background: $card-bg;
  border-right: 1px solid $border;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid $border;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: $text;
}

.header-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: $text-muted;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 1px solid $border;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  background: $bg;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 7px 10px;
}

.search-icon { color: $text-muted; margin-right: 8px; font-size: 12px; }

.search-input {
  flex: 1; border: none; background: transparent; outline: none;
  font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; color: $text;
  &::placeholder { color: #CBD5E1; }
}

.btn-nuevo {
  width: 34px; height: 34px; border: none; border-radius: 8px; background: $success; color: white;
  cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; font-size: 13px;
  &:hover { background: darken($success, 8%); }
}

.btn-search-google, .btn-filter {
  width: 34px; height: 34px; border: 1px solid $border; border-radius: 8px; background: $card-bg; color: $text-muted;
  cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; font-size: 13px;
  &:hover { background: $bg; }
  &.active { background: $primary; color: white; border-color: $primary; }
}

.label-badge-row { padding: 8px 14px; border-bottom: 1px solid $border; }
.label-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; }

.email-items { flex: 1; overflow-y: auto; }

.loading-container { display: flex; justify-content: center; align-items: center; height: 100px; }
.spinner { width: 28px; height: 28px; border: 3px solid $border; border-top-color: $primary; border-radius: 50%; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; color: $text-muted;
  .empty-icon { font-size: 36px; margin-bottom: 10px; color: #CBD5E1; }
}

.email-item {
  padding: 10px 14px; border-bottom: 1px solid lighten($border, 3%); cursor: pointer; transition: all 0.15s;
  &:hover { background: $bg; }
  &.selected { background: rgba($primary, 0.04); border-left: 3px solid $primary; }
  &.unread { position: relative; padding-left: 22px;
    &::before { content: ''; position: absolute; left: 8px; top: 50%; transform: translateY(-50%); width: 7px; height: 7px; background: $primary; border-radius: 50%; }
  }
  &.from-search { border-right: 3px solid $success; }
}

.email-item-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
.sender-info { display: flex; align-items: center; gap: 5px; }
.tone-icon { font-size: 13px; &.tone-positive { color: $success; } &.tone-negative { color: $danger; } &.tone-neutral { color: $warning; } }
.sender-name { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: $text; }
.email-meta { display: flex; align-items: center; gap: 5px; }
.email-time { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: $primary; font-weight: 600; }
.email-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; padding: 2px 6px; border-radius: 4px; }
.email-subject { font-size: 13px; font-weight: 500; color: $text; margin-bottom: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.email-preview { font-size: 12px; color: $text-muted; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

// Panel de vista de email
.email-view-panel { flex: 1; background: $bg; display: flex; flex-direction: column; height: 100%; overflow: hidden; }

.view-header {
  display: flex; justify-content: space-between; align-items: center; height: 56px; padding: 0 22px;
  background: $card-bg; border-bottom: 1px solid $border;
}

.view-actions, .more-actions { display: flex; gap: 6px; }

.action-btn {
  display: flex; align-items: center; gap: 5px; padding: 6px 12px; border: 1px solid $border; border-radius: 8px;
  background: $card-bg; color: $text; font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 500; cursor: pointer; transition: all 0.15s;
  &:hover { background: $bg; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &.primary { background: $primary; border-color: $primary; color: white; }
}

.email-content { flex: 1; padding: 28px; overflow-y: auto; }

.sender-row { display: flex; align-items: center; gap: 14px; margin-bottom: 20px; }
.avatar { width: 44px; height: 44px; border-radius: 50%; background: rgba($primary, 0.08); display: flex; align-items: center; justify-content: center; font-family: 'JetBrains Mono', monospace; font-size: 14px; font-weight: 700; color: $primary; }
.sender-details { flex: 1; display: flex; flex-direction: column; gap: 1px; }
.sender-full-name { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 700; color: $text; }
.sender-email { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $text-muted; }
.email-date { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: $text-muted; }

.email-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 800; color: $text; margin: 0 0 20px 0; line-height: 1.3; }
.divider { height: 1px; background: $border; margin-bottom: 20px; }

// AI Panel
.ai-panel { background: $card-bg; border: 1px solid $border; border-radius: 10px; padding: 16px; margin-bottom: 20px; }
.btn-ai {
  display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: $text; color: white;
  border: none; border-radius: 8px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s;
  &:hover { background: lighten($text, 10%); }
}
.ai-results { margin-top: 14px; p { margin: 0 0 6px 0; font-size: 13px; color: $text; line-height: 1.5; } }
.ai-response { margin-top: 12px; padding-top: 12px; border-top: 1px solid $border; }
.response-text { margin-top: 8px; font-size: 13px; line-height: 1.7; color: $text; }
.loading-ai { display: flex; justify-content: center; align-items: center; height: 50px; i { font-size: 22px; color: $primary; } }

.ai-input-group { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 8px 12px; background: $bg; border: 1px solid $border; border-radius: 8px; }
.input-icon { color: $text-muted; font-size: 14px; }
.ai-input { flex: 1; border: none; background: transparent; outline: none; font-size: 13px; color: $text; &::placeholder { color: #CBD5E1; } }
.btn-send { width: 30px; height: 30px; border: none; border-radius: 6px; background: $primary; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; &:hover { background: $primary-dark; } }

// Thread
.thread-container { background: $card-bg; border: 1px solid $border; border-radius: 10px; padding: 16px; margin-bottom: 20px; }
.thread-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid $border; h4 { margin: 0; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; color: $text; i { margin-right: 6px; color: $primary; } } }
.btn-close-thread { width: 26px; height: 26px; border: 1px solid $border; border-radius: 6px; background: $card-bg; cursor: pointer; color: $text-muted; display: flex; align-items: center; justify-content: center; font-size: 11px; &:hover { background: $bg; } }
.thread-message { border: 1px solid $border; border-radius: 8px; margin-bottom: 10px; overflow: hidden; &.current { border-color: $primary; box-shadow: 0 2px 8px rgba($primary, 0.12); } }
.thread-msg-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: $bg; border-bottom: 1px solid $border; }
.thread-sender { display: flex; align-items: center; gap: 6px; strong { color: $text; font-size: 13px; } }
.thread-email { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: $text-muted; }
.thread-date { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: $text-muted; }
.thread-subject { padding: 6px 12px; font-size: 12px; color: $text-muted; border-bottom: 1px solid $border; }
.thread-iframe { width: 100%; height: 200px; border: none; background: $card-bg; }

// Attachments
.attachments { margin-bottom: 20px; strong { display: block; margin-bottom: 8px; font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: $text-muted; } }
.attachment-list { display: flex; flex-wrap: wrap; gap: 8px; }
.attachment-item {
  .attachment-thumb { max-width: 80px; border: 1px solid $border; border-radius: 6px; cursor: pointer; }
  .attachment-link { display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; background: $bg; border: 1px solid $border; border-radius: 8px; color: $text; text-decoration: none; font-family: 'JetBrains Mono', monospace; font-size: 12px; &:hover { background: $border; } }
}

.email-iframe { width: 100%; height: 50vh; border: none; background: $card-bg; border-radius: 8px; }

.reply-footer { text-align: center; margin-top: 20px; }
.btn-reply {
  display: inline-flex; align-items: center; gap: 8px; padding: 10px 24px; background: $primary; color: white;
  border: none; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s;
  box-shadow: 0 2px 8px rgba($primary, 0.25);
  &:hover { background: $primary-dark; transform: translateY(-1px); box-shadow: 0 4px 12px rgba($primary, 0.3); }
}

.empty-view {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: $text-muted;
  .empty-icon-large { font-size: 48px; margin-bottom: 14px; color: #CBD5E1; }
  h3 { margin: 0; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 500; }
}

// Modal styles
.scroll-listado { overflow-y: auto; max-height: calc(100vh - 200px); }
.group-item-email { cursor: pointer; transition: background 0.15s; &:hover { background: $bg; } }

// Modal responder
.reply-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 0 14px; margin-bottom: 14px; border-bottom: 1px solid $border;

  .reply-header-info { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .reply-title {
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 17px; font-weight: 700; color: $text;
    i { color: $primary; margin-right: 6px; }
  }
  .reply-to-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba($primary, 0.08); color: $primary-dark;
    border: 1px solid rgba($primary, 0.25);
    padding: 4px 12px; border-radius: 999px; font-size: 13px;
    strong { font-weight: 600; }
  }
  .reply-close {
    border: none; background: transparent; color: $text-muted; font-size: 16px;
    width: 32px; height: 32px; border-radius: 8px; cursor: pointer;
    &:hover { background: $bg; color: $text; }
  }
}

.plantillas-panel {
  background: $bg; border: 1px solid $border; border-radius: 10px; padding: 12px;
  height: 100%; display: flex; flex-direction: column;
}
.plantillas-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
  .plantillas-title { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; color: $text; }
  .plantillas-count {
    font-family: 'JetBrains Mono', monospace; font-size: 11px; color: $text-muted;
    background: $card-bg; border: 1px solid $border; padding: 1px 8px; border-radius: 999px;
  }
}
.plantillas-search {
  display: flex; align-items: center; gap: 8px; background: $card-bg; border: 1px solid $border;
  border-radius: 8px; padding: 7px 10px; margin-bottom: 10px;
  i { color: $text-muted; font-size: 12px; }
  input {
    border: none; outline: none; background: transparent; width: 100%; font-size: 13px; color: $text;
    &::placeholder { color: $text-muted; }
  }
}
.btn-chloe {
  width: 100%; border: none; border-radius: 8px; padding: 8px 10px; margin-bottom: 10px;
  background: linear-gradient(135deg, #8B5CF6, #6366F1); color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  i { margin-right: 6px; }
  &:hover { opacity: 0.9; transform: translateY(-1px); }
}
.plantillas-list {
  flex: 1; max-height: calc(100vh - 320px);
  display: flex; flex-direction: column; gap: 6px; padding-right: 2px;
}
.plantilla-item {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  background: $card-bg; border: 1px solid $border; border-radius: 8px; padding: 9px 10px;
  cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s;
  .plantilla-nombre { font-size: 13px; color: $text; line-height: 1.3; }
  &:hover { border-color: $primary; box-shadow: 0 2px 8px rgba($primary, 0.12); }
}
.plantilla-ai {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 6px; border: none; cursor: pointer;
  background: rgba($primary, 0.1); color: $primary;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s;
  &:hover { background: $primary; color: #fff; }
}
.plantillas-empty {
  text-align: center; color: $text-muted; font-size: 13px; padding: 18px 0;
  i { margin-right: 6px; }
}
.reply-send-row { display: flex; justify-content: flex-end; margin-top: 16px; }

@keyframes fa-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.fa-spin { animation: fa-spin 1s linear infinite; }
.fa-spin-custom { animation: fa-spin 1s linear infinite; }
</style>

<style lang="scss">
@import "@core/scss/vue/libs/quill.scss";

.modal-max-height .modal-dialog {
  height: 100vh;
  max-height: 100vh;
  margin: 0;
}

.modal-max-height .modal-content {
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
}

.editor-nuevo-email .ql-editor {
  min-height: 250px;
}

/* Modal pedidos Shopify */
.shopify-order-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.shopify-order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.shopify-order-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.shopify-order-name {
  font-weight: 600;
  font-size: 1rem;
}

.shopify-order-name i {
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.shopify-order-date {
  color: #94a3b8;
  font-size: 0.85rem;
}

.shopify-order-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.shopify-order-total {
  font-size: 1rem;
  margin-left: 0.5rem;
}

.shopify-tracking {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.25rem;

  i {
    font-size: 0.75rem;
  }
}

.shopify-line-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;

  & + .shopify-line-item {
    border-top: 1px solid #f1f5f9;
  }
}

.shopify-line-thumb {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8fafc;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.shopify-line-info {
  flex: 1;
  min-width: 0;
}

.shopify-line-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.shopify-line-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.shopify-line-variant {
  background: #f1f5f9;
  border-radius: 6px;
  padding: 0.1rem 0.5rem;
  font-size: 0.8rem;
}

.shopify-line-sku {
  color: #94a3b8;
  font-size: 0.8rem;
}

.shopify-line-prices {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
}

.shopify-line-unit {
  color: #94a3b8;
  font-size: 0.85rem;
}

.shopify-line-original {
  color: #cbd5e1;
  margin-right: 0.25rem;
}

.shopify-line-total {
  font-weight: 600;
  font-size: 0.9rem;
}

.dark-layout {
  .shopify-order-card { border-color: #334155; }
  .shopify-order-header { border-bottom-color: #334155; }
  .shopify-line-item + .shopify-line-item { border-top-color: #1e293b; }
  .shopify-line-thumb { border-color: #334155; background: #1e293b; }
  .shopify-line-variant { background: #1e293b; }
}

.dark-layout {
  .email-manager { background: #0F172A !important; border-color: #334155 !important; }
  .email-list-panel { background: #1E293B !important; border-right-color: #334155 !important; }
  .list-header { border-bottom-color: #334155 !important; }
  .header-title { color: #E2E8F0 !important; }
  .header-count { color: #94A3B8 !important; }
  .search-row { border-bottom-color: #334155 !important; }
  .search-box { background: #0F172A !important; border-color: #334155 !important; }
  .search-input { color: #E2E8F0 !important; }
  .email-item { border-bottom-color: #334155 !important; &:hover { background: rgba(255,255,255,0.03) !important; } &.selected { background: rgba(#3B82F6, 0.06) !important; } }
  .sender-name { color: #E2E8F0 !important; }
  .email-subject { color: #E2E8F0 !important; }
  .email-preview { color: #64748B !important; }
  .email-time { color: #60A5FA !important; }
  .group-item-email { &:hover { background: rgba(255,255,255,0.03) !important; } }
  .btn-search-google, .btn-filter { background: #1E293B !important; border-color: #475569 !important; color: #94A3B8 !important; &:hover { background: #334155 !important; } &.active { background: #3B82F6 !important; border-color: #3B82F6 !important; color: white !important; } }
  .btn-nuevo { background: #10B981 !important; }
  .email-view-panel { background: #0F172A !important; }
  .view-header { background: #1E293B !important; border-bottom-color: #334155 !important; }
  .action-btn { background: #1E293B !important; border-color: #475569 !important; color: #E2E8F0 !important; &:hover { background: #334155 !important; } &.primary { background: #3B82F6 !important; border-color: #3B82F6 !important; } }
  .email-content { background: #0F172A !important; }
  .sender-full-name { color: #E2E8F0 !important; }
  .sender-email { color: #94A3B8 !important; }
  .email-date { color: #94A3B8 !important; }
  .email-title { color: #E2E8F0 !important; }
  .divider { background: #334155 !important; }
  .avatar { background: rgba(#3B82F6, 0.1) !important; color: #60A5FA !important; }
  .ai-panel { background: #1E293B !important; border-color: #334155 !important; }
  .ai-results p, .response-text { color: #E2E8F0 !important; }
  .ai-response { border-top-color: #334155 !important; }
  .ai-input-group { background: #0F172A !important; border-color: #334155 !important; }
  .ai-input { color: #E2E8F0 !important; }
  .thread-container { background: #1E293B !important; border-color: #334155 !important; }
  .thread-header { border-bottom-color: #334155 !important; h4 { color: #E2E8F0 !important; } }
  .thread-message { border-color: #334155 !important; &.current { border-color: #3B82F6 !important; } }
  .thread-msg-header { background: #0F172A !important; border-bottom-color: #334155 !important; strong { color: #E2E8F0 !important; } }
  .btn-close-thread { background: #1E293B !important; border-color: #475569 !important; color: #94A3B8 !important; }
  .thread-iframe, .email-iframe { background: #1E293B !important; }
  .attachment-link { background: #334155 !important; border-color: #475569 !important; color: #94A3B8 !important; }
  .empty-state { color: #94A3B8 !important; }
  .reply-modal-header { border-bottom-color: #334155 !important; }
  .reply-title { color: #E2E8F0 !important; }
  .reply-to-chip { background: rgba(#3B82F6, 0.15) !important; color: #93C5FD !important; border-color: rgba(#3B82F6, 0.4) !important; }
  .reply-close:hover { background: #334155 !important; color: #E2E8F0 !important; }
  .plantillas-panel { background: #0F172A !important; border-color: #334155 !important; }
  .plantillas-title { color: #E2E8F0 !important; }
  .plantillas-count { background: #1E293B !important; border-color: #334155 !important; color: #94A3B8 !important; }
  .plantillas-search { background: #1E293B !important; border-color: #334155 !important; input { color: #E2E8F0 !important; } }
  .plantilla-item { background: #1E293B !important; border-color: #334155 !important; .plantilla-nombre { color: #E2E8F0 !important; } }
}
</style>
